import { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Document } from '../types';

interface DeleteConfirmModalProps {
  document: Document;
  onConfirm: () => void;
  onCancel: () => void;
}

export const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  document,
  onConfirm,
  onCancel,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    // Valider si l'utilisateur a saisi "supprimer" ou "delete" (case insensitive)
    const normalized = inputValue.trim().toLowerCase();
    setIsValid(normalized === 'supprimer' || normalized === 'delete');
  }, [inputValue]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-error-100">
              <AlertTriangle className="h-5 w-5 text-error-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-neutral-900">
                Confirmer la suppression
              </h2>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <p className="mb-2 text-sm text-neutral-600">
              Vous êtes sur le point de supprimer définitivement le document :
            </p>
            <p className="mb-4 rounded-lg bg-neutral-100 p-3 font-medium text-neutral-900">
              {document.name}
            </p>
            <p className="mb-4 text-sm text-neutral-600">
              Cette action est <span className="font-semibold text-error-600">irréversible</span>.
              Pour confirmer, veuillez saisir{' '}
              <span className="font-mono font-semibold text-neutral-900">supprimer</span> ou{' '}
              <span className="font-mono font-semibold text-neutral-900">delete</span> ci-dessous :
            </p>
          </div>

          <div>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Saisissez 'supprimer' ou 'delete'"
              className="w-full rounded-lg border border-neutral-300 px-4 py-2 text-sm transition-colors focus:border-error-500 focus:outline-none focus:ring-2 focus:ring-error-500/20"
              autoFocus
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" onClick={onCancel} variant="outline">
              Annuler
            </Button>
            <Button
              type="submit"
              variant="error"
              disabled={!isValid}
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Supprimer définitivement
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
